import { lazy } from "react";

// Layout Components
// import Layout from "../Layout"; // We will import Layout in App.js to wrap the routes, or here if we want to define it in the config

// Components
import Home from "../views/Home";
import Login from "../views/Login";
import BulkRate from "../views/RateManagement/BulkRate";
import Unauthorized from "../views/Unauthorized";

// Lazy-loaded Components (Site Management)
const RatePlan = lazy(() => import("../views/SiteManagement/RatePlan"));
const RatePlanCreate = lazy(() => import("../views/SiteManagement/RatePlan/CreateRatePlan"));
const Country = lazy(() => import("../views/SiteManagement/Country"));
const CreateCountry = lazy(() => import("../views/SiteManagement/Country/Create"));
const State = lazy(() => import("../views/SiteManagement/State"));
const CreateState = lazy(() => import("../views/SiteManagement/State/Create"));
const City = lazy(() => import("../views/SiteManagement/City"));
const CreateCity = lazy(() => import("../views/SiteManagement/City/Create"));
const Location = lazy(() => import("../views/SiteManagement/Location"));
const CreateLocation = lazy(() => import("../views/SiteManagement/Location/Create"));
const PaymentType = lazy(() => import("../views/SiteManagement/PaymentType"));
const CreatePaymentType = lazy(() => import("../views/SiteManagement/PaymentType/Create"));
const Customer = lazy(() => import("../views/SiteManagement/Customer"));
const CreateCustomer = lazy(() => import("../views/SiteManagement/Customer/Create"));
const Commission = lazy(() => import("../views/SiteManagement/commission"));
const CreateCommission = lazy(() => import("../views/SiteManagement/commission/Create"));
const TravelPartners = lazy(() => import("../views/SiteManagement/TravelPartners"));
const CreateTravelPartner = lazy(() => import("../views/SiteManagement/TravelPartners/Create"));
const CreateCompanyDetails = lazy(() => import("../views/SiteManagement/CompanyDetails/Create"));

// Hotel Management
const HotelList = lazy(() => import("../views/HotelsManagement/HotelList"));
const HotelCreate = lazy(() => import("../views/HotelsManagement/HotelList/Create"));

// Room Management
const RoomCreate = lazy(() => import("../views/RoomManagement/RoomList/Create"));

// Document Management
const DocumentList = lazy(() => import("../views/DocumentManagement/DocumentList"));
const DocumentCreate = lazy(() => import("../views/DocumentManagement/DocumentList/Create"));

// Inclusion Management
const InclusionList = lazy(() => import("../views/Inclusion"));
const InclusionCreate = lazy(() => import("../views/Inclusion/Create"));

// Rate Management
const RateLists = lazy(() => import("../views/RateManagement/RateHotelList"));
const RateDashboardLists = lazy(() => import("../views/RateManagement"));

// Reservation Management
const ReservationList = lazy(() => import("../views/ReservationManagement/ReservationList"));
const ReservationCreate = lazy(() => import("../views/ReservationManagement/ReservationList/Create"));
const ReservationView = lazy(() => import("../views/ReservationManagement/ReservationList/ReservationView"));
const ConfirmReservation = lazy(() => import("../views/ReservationManagement/ReservationList/ConfirmReservation"));
const HotelVouchers = lazy(() => import("../views/ReservationManagement/vouchers/HotelVouchers"));
const AdminVouchers = lazy(() => import("../views/ReservationManagement/vouchers/AdminVouchers"));
const CustomerVouchers = lazy(() => import("../views/ReservationManagement/vouchers/CustomerVouchers"));
const ReservationInvoice = lazy(() => import("../views/Payment/invoice/PaymentInvoice"));
const SendEmailOptions = lazy(() => import("../views/ReservationManagement/ReservationList/SendEmailOptions"));

// Payment Management
const PaymentList = lazy(() => import("../views/Payment/PaymentList/"));
const PaymentCreate = lazy(() => import("../views/Payment/PaymentList/Create"));

// Bank Management
const BankStatementList = lazy(() => import("../views/BankManagement/BankStatementList"));
const BankStatementCreate = lazy(() => import("../views/BankManagement/BankStatementList/Create"));
const BankStatementCreateTransaction = lazy(() => import("../views/BankManagement/BankStatementList/CreateTransaction"));
const BankStatementView = lazy(() => import("../views/BankManagement/BankStatementList/View"));
const PaymentView = lazy(() => import("../views/Payment/PaymentList/View"));
const PaymentInvoice = lazy(() => import("../views/Payment/invoice/PaymentReceiptRouter"));

// GST Invoices Management (new)
const GSTInvoiceList = lazy(() => import("../views/GSTManagement/GSTInvoiceList"));

//Hotel Invoice management
const HotelInvoiceList = lazy(() => import("../views/HotelInvoices"));
const HotelInvoiceCreate = lazy(() => import("../views/HotelInvoices/create"));

//Customer Invoice management
const CustomerInvoiceList = lazy(() => import("../views/CustomerInvoices"));
const CustomerInvoiceCreate = lazy(() => import("../views/CustomerInvoices/create"));

//Expenses management
const ExpensesList = lazy(() => import("../views/Expenses"));
const ExpensesCreate = lazy(() => import("../views/Expenses/create"));

//Hotel User management
const HotelUserList = lazy(() => import("../views/HotelUser"));
const HotelUserCreate = lazy(() => import("../views/HotelUser/create"));

//Crs User management
const CrsUserList = lazy(() => import("../views/CrsUser"));
const CreateCrsUser = lazy(() => import("../views/CrsUser/create"));

//OTA Management
const OtaManagementList = lazy(() => import("../views/OtaManagement"));
const CreateOtaListing = lazy(() => import("../views/OtaManagement/create"));
const ViewOtaListing = lazy(() => import("../views/OtaManagement/view"));

//Reports management
const ReportsList = lazy(() => import("../views/Reports"));
const ReportsCreate = lazy(() => import("../views/Reports/create"));

//Profile management
const Profile = lazy(() => import("../views/Profile"));
const EditProfile = lazy(() => import("../views/Profile/EditProfile"));
const ChangePassword = lazy(() => import("../views/Profile/ChangePassword"));

//Inquiry Management
const InquiryList = lazy(() => import("../views/InquiryManagement/Index"));
const InquiryCreate = lazy(() => import("../views/InquiryManagement/Create"));
const InquiryView = lazy(() => import("../views/InquiryManagement/View"));

//Role Management
const RoleList = lazy(() => import("../views/RoleManagement"));
const RoleCreate = lazy(() => import("../views/RoleManagement/Create"));

const routesConfig = [
    // Public Routes
    { path: "", element: <Home />, protected: false, layout: true },
    { path: "login", element: <Login />, protected: false },
    { path: "dashboard", element: <Home />, protected: true, layout: true },
    { path: "unauthorized", element: <Unauthorized />, protected: false },

    // Site Management
    { path: "site-management/rate-plan/", element: <RatePlan />, protected: true, layout: true, permission: "rate_plan:view" },
    { path: "site-management/rate-plan/create/", element: <RatePlanCreate />, protected: true, layout: true, permission: "rate_plan:create" },
    { path: "site-management/rate-plan/update/:id", element: <RatePlanCreate />, protected: true, layout: true, permission: "rate_plan:edit" },
    { path: "site-management/country", element: <Country />, protected: true, layout: true, permission: "country:view" },
    { path: "site-management/country/create", element: <CreateCountry />, protected: true, layout: true, permission: "country:create" },
    { path: "site-management/country/update/:id", element: <CreateCountry />, protected: true, layout: true, permission: "country:edit" },
    { path: "site-management/state", element: <State />, protected: true, layout: true, permission: "state:view" },
    { path: "site-management/state/create", element: <CreateState />, protected: true, layout: true, permission: "state:create" },
    { path: "site-management/state/update/:id", element: <CreateState />, protected: true, layout: true, permission: "state:edit" },
    { path: "site-management/city", element: <City />, protected: true, layout: true, permission: "city:view" },
    { path: "site-management/city/create", element: <CreateCity />, protected: true, layout: true, permission: "city:create" },
    { path: "site-management/city/update/:id", element: <CreateCity />, protected: true, layout: true, permission: "city:edit" },
    { path: "site-management/location", element: <Location />, protected: true, layout: true, permission: "location:view" },
    { path: "site-management/location/create", element: <CreateLocation />, protected: true, layout: true, permission: "location:create" },
    { path: "site-management/location/update/:id", element: <CreateLocation />, protected: true, layout: true, permission: "location:edit" },
    { path: "site-management/payment-type", element: <PaymentType />, protected: true, layout: true, permission: "payment_type:view" },
    { path: "site-management/payment-type/create", element: <CreatePaymentType />, protected: true, layout: true, permission: "payment_type:create" },
    { path: "site-management/payment-type/update/:id", element: <CreatePaymentType />, protected: true, layout: true, permission: "payment_type:edit" },
    { path: "site-management/customer", element: <Customer />, protected: true, layout: true, permission: "customer:view" },
    { path: "site-management/customer/create", element: <CreateCustomer />, protected: true, layout: true, permission: "customer:create" },
    { path: "site-management/customer/update/:id", element: <CreateCustomer />, protected: true, layout: true, permission: "customer:edit" },
    { path: "site-management/commission", element: <Commission />, protected: true, layout: true, permission: "commission:view" },
    { path: "site-management/commission/create", element: <CreateCommission />, protected: true, layout: true, permission: "commission:create" },
    { path: "site-management/commission/update/:id", element: <CreateCommission />, protected: true, layout: true, permission: "commission:edit" },
    { path: "site-management/travel-partners", element: <TravelPartners />, protected: true, layout: true, permission: "travel_partners:view" },
    { path: "site-management/travel-partners/create", element: <CreateTravelPartner />, protected: true, layout: true, permission: "travel_partners:create" },
    { path: "site-management/travel-partners/update/:id", element: <CreateTravelPartner />, protected: true, layout: true, permission: "travel_partners:edit" },
    { path: "site-management/company-details", element: <CreateCompanyDetails />, protected: true, layout: true, permission: "company_details:view" },

    // Hotel Management
    { path: "hotel-management/hotel", element: <HotelList />, protected: true, layout: true, permission: "hotel:view" },
    { path: "hotel-management/hotel/create", element: <HotelCreate />, protected: true, layout: true, permission: "hotel:create" },
    { path: "hotel-management/hotel/update/:id", element: <HotelCreate />, protected: true, layout: true, permission: "hotel:edit" },

    // Room Management (Permissions assumed covered by HOTEL or similar as implied by previous context, but will omit if unsure. User didn't list specific ROOM permissions.)
    // Wait, checking snippet again... No ROOM permissions.
    // I will leave them as standard protected routes for now.
    { path: "room-management/room/create", element: <RoomCreate />, protected: true, layout: true, permission: "hotel:create" },
    { path: "room-management/room/update/:id", element: <RoomCreate />, protected: true, layout: true, permission: "hotel:edit" },

    // Document Management
    { path: "document-management/document-list", element: <DocumentList />, protected: true, layout: true, permission: "document:view" },
    { path: "document-management/document/create", element: <DocumentCreate />, protected: true, layout: true, permission: "document:create" },
    { path: "document-management/document/update/:id", element: <DocumentCreate />, protected: true, layout: true, permission: "document:edit" },

    // Inclusion Management
    { path: "site-management/inclusion", element: <InclusionList />, protected: true, layout: true, permission: "inclusion:view" },
    { path: "site-management/inclusion/create", element: <InclusionCreate />, protected: true, layout: true, permission: "inclusion:create" },
    { path: "site-management/inclusion/update/:id", element: <InclusionCreate />, protected: true, layout: true, permission: "inclusion:edit" },

    // Rate Management
    { path: "rate-management/list", element: <RateLists />, protected: true, layout: true, permission: "rate:view" },
    { path: "rate-management/dashboard/list/:hotelId", element: <RateDashboardLists />, protected: true, layout: true, permission: "rate:view" },
    { path: "rate-management/bulk/:hotelId/:tab", element: <BulkRate />, protected: true, layout: true, permission: "rate:edit" },

    // Reservation Management
    { path: "reservation-management/list", element: <ReservationList />, protected: true, layout: true, permission: "reservation:view" },
    { path: "reservation-management/create", element: <ReservationCreate />, protected: true, layout: true, permission: "reservation:create" },
    { path: "reservation-management/update/:id", element: <ReservationCreate />, protected: true, layout: true, permission: "reservation:edit" },
    { path: "reservation-management/view/:id", element: <ReservationView />, protected: true, layout: true, permission: "reservation:view" },
    { path: "reservation-management/reservation/confirm/:id", element: <ConfirmReservation />, protected: false }, // Explicitly unprotected in App.js
    { path: "reservation-management/hotel-vouchers/:hotelId", element: <HotelVouchers />, protected: true, layout: false, permission: "reservation:hotel_voucher" },
    { path: "reservation-management/admin-vouchers/:hotelId", element: <AdminVouchers />, protected: true, layout: false, permission: "reservation:admin_voucher" },
    { path: "reservation-management/invoice/:id", element: <ReservationInvoice />, protected: true, layout: false, permission: "reservation:view" },
    { path: "reservation-management/send-email/:id", element: <SendEmailOptions />, protected: true, layout: true, permission: "reservation:send_email" },
    { path: "reservation-management/customer-vouchers/:hotelId", element: <CustomerVouchers />, protected: true, layout: false, permission: "reservation:customer_voucher" },

    // GST Management
    { path: "gst-management/invoices", element: <GSTInvoiceList />, protected: true, layout: true, permission: "gst:view" },

    // Expenses Management
    { path: "expenses/list", element: <ExpensesList />, protected: true, layout: true, permission: "expense:view" },
    { path: "expenses/create", element: <ExpensesCreate />, protected: true, layout: true, permission: "expense:create" },
    { path: "expenses/edit/:id", element: <ExpensesCreate />, protected: true, layout: true, permission: "expense:edit" },

    // Hotel Invoices
    { path: "hotel-invoices/list", element: <HotelInvoiceList />, protected: true, layout: true, permission: "hotel_invoices:view" },
    { path: "hotel-invoices/create", element: <HotelInvoiceCreate />, protected: true, layout: true, permission: "hotel_invoices:create" },
    { path: "hotel-invoices/edit/:id", element: <HotelInvoiceCreate />, protected: true, layout: true, permission: "hotel_invoices:edit" },

    // Customer Invoices
    { path: "customer-invoices/list", element: <CustomerInvoiceList />, protected: true, layout: true, permission: "customer:view" }, // Using CUSTOMER permission based on naming
    { path: "customer-invoices/create", element: <CustomerInvoiceCreate />, protected: true, layout: true, permission: "customer:create" },

    // Hotel User Management
    { path: "hotel-user/list", element: <HotelUserList />, protected: true, layout: true, permission: "hotel_users:view" },
    { path: "hotel-user/create", element: <HotelUserCreate />, protected: true, layout: true, permission: "hotel_users:create" },
    { path: "hotel-user/edit/:id", element: <HotelUserCreate />, protected: true, layout: true, permission: "hotel_users:edit" },

    // CRS User Management
    { path: "crs-user/list", element: <CrsUserList />, protected: true, layout: true, permission: "crs_users:view" },
    { path: "crs-user/create", element: <CreateCrsUser />, protected: true, layout: true, permission: "crs_users:create" },
    { path: "crs-user/edit/:id", element: <CreateCrsUser />, protected: true, layout: true, permission: "crs_users:edit" },

    // OTA Management
    { path: "ota-management/list", element: <OtaManagementList />, protected: true, layout: true, permission: "ota:view" },
    { path: "ota-management/create", element: <CreateOtaListing />, protected: true, layout: true, permission: "ota:create" },
    { path: "ota-management/edit/:id", element: <CreateOtaListing />, protected: true, layout: true, permission: "ota:edit" },
    { path: "ota-management/view/:id", element: <ViewOtaListing />, protected: true, layout: true, permission: "ota:view" },

    // Reports
    { path: "reports/list", element: <ReportsList />, protected: true, layout: true }, // No specific permissions in list provided
    { path: "reports/create", element: <ReportsCreate />, protected: true, layout: true },

    // Payment Management
    { path: "payment/list", element: <PaymentList />, protected: true, layout: true, permission: "payment:view" },
    { path: "payment/create", element: <PaymentCreate />, protected: true, layout: true, permission: "payment:create" },
    { path: "payment/view/:id", element: <PaymentView />, protected: true, layout: true, permission: "payment:view" },
    { path: "payment/edit/:id", element: <PaymentCreate />, protected: true, layout: true, permission: "payment:edit" },
    { path: "payment/receipt/:id", element: <PaymentInvoice />, protected: true, layout: false, permission: "payment:receipt" },

    // Bank Management
    { path: "bank-management/bank-statements", element: <BankStatementList />, protected: true, layout: true, permission: "bank:view" },
    { path: "bank-management/bank-statements/create", element: <BankStatementCreate />, protected: true, layout: true, permission: "bank:create" },
    { path: "bank-management/bank-statements/view/:id", element: <BankStatementView />, protected: true, layout: true, permission: "bank:view" },
    { path: "bank-management/bank-statements/update/:id", element: <BankStatementCreateTransaction />, protected: true, layout: true, permission: "bank:edit" },

    // Inquiry Management
    { path: "inquiry-management/list", element: <InquiryList />, protected: true, layout: true, permission: "inquiry:view" },
    { path: "inquiry-management/create", element: <InquiryCreate />, protected: true, layout: true, permission: "inquiry:create" },
    { path: "inquiry-management/update/:id", element: <InquiryCreate />, protected: true, layout: true, permission: "inquiry:edit" },
    { path: "inquiry-management/view/:id", element: <InquiryView />, protected: true, layout: true, permission: "inquiry:view" },

    // Role Management
    { path: "role-management/list", element: <RoleList />, protected: true, layout: true, permission: "role:view" },
    { path: "role-management/create", element: <RoleCreate />, protected: true, layout: true, permission: "role:create" },
    { path: "role-management/edit/:id", element: <RoleCreate />, protected: true, layout: true, permission: "role:edit" },

    // Profile
    { path: "profile", element: <Profile />, protected: true, layout: true },
    { path: "profile/edit", element: <EditProfile />, protected: true, layout: true },
    { path: "profile/change-password", element: <ChangePassword />, protected: true, layout: true },
];

export default routesConfig;
